import { t as coreT } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { SBP_PAY_TEST_STATUS_OPTIONS } from 'modules/organizer/domain/constants/wallet-topup';
import {
  sbpPayTestFormValidator,
  SbpPayTestFormValues,
  SbpPayTestStatusOption,
} from 'modules/organizer/domain/validators/sbp-pay-test.validator';
import { useSimulateWalletTopupSbpPayMutation } from 'modules/organizer/store/billing-api';
import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { theme } from 'styles/theme/theme';
import UDFormSelect from 'ud-form/components/select';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';

interface SubscriptionSbpPayTestFormProps {
  topupId: number;
}

const statusOptions: SbpPayTestStatusOption[] = SBP_PAY_TEST_STATUS_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

export const SubscriptionSbpPayTestForm: React.FC<SubscriptionSbpPayTestFormProps> = ({ topupId }) => {
  const [simulateSbpPay, simulateState] = useSimulateWalletTopupSbpPayMutation();
  const { t } = useLingui();

  const handleSubmit = async (values: SbpPayTestFormValues, helpers: FormikHelpers<SbpPayTestFormValues>) => {
    helpers.setStatus(undefined);
    try {
      await simulateSbpPay({
        id: topupId,
        status: values.status.value,
      }).unwrap();
    } catch (submitError) {
      helpers.setStatus(extractErrorMessage(submitError));
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <div className="border-darkBlue-100 mt-2 flex w-full flex-col gap-3 border-t pt-4">
      <UDText type="subhead">
        <Trans>Подтвердить оплату:</Trans>
      </UDText>
      <Formik<SbpPayTestFormValues>
        initialValues={{ status: statusOptions[0] }}
        validationSchema={sbpPayTestFormValidator}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, status }) => (
          <Form className="flex flex-col gap-3">
            <UDFormSelect name="status" isMulti={false} options={statusOptions} placeholder={t`Статус`} />
            {status && (
              <UDText type="footnote" color={theme.colors.CaseErrorText}>
                {status}
              </UDText>
            )}
            <UDButton type="submit" variant="secondary" loading={isSubmitting || simulateState.isLoading}>
              <Trans>Протестировать</Trans>
            </UDButton>
          </Form>
        )}
      </Formik>
    </div>
  );
};

function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message) {
    return error.message;
  }

  return coreT`Не удалось симулировать оплату.`;
}
